import { get } from '@koob/utils';
import { SEARCH_TYPES } from './enums';

export default function search({
  query,
  types = Object.values(SEARCH_TYPES),
  fromOrganizationId,
  scopeDmcId,
  isMarketPlaceSource = false,
  skip = false,
  searchedCountries = [],
  page,
  shouldSearchAdditionalFields = true,
  locale,
  includeUnavailable = false,
}) {
  if (!query?.length > 0 || skip) {
    return Promise.resolve({ data: [] });
  }

  // encodeURIComponent so a query containing "&" (e.g. "hotel & spa") is not
  // read as a query-string separator, which truncated the search to "hotel".
  // Mid-word queries ("Ho C") are handled API-side by a bool_prefix clause
  // (KD-3535) — deploy that API change before/with this file.
  let url = `/api/internal/search?query=${encodeURIComponent(
    query,
  )}&types=${types.join(',')}`;

  if (fromOrganizationId) {
    url += `&organizationId=${fromOrganizationId}`;
  }

  // KD-3977 — hotels of this DMC and of its marketplace partners; unlike
  // fromOrganizationId, which only keeps what the organization owns.
  if (scopeDmcId) {
    url += `&dmcId=${scopeDmcId}`;
  }

  // Without it the API searches the EN index only — typing a translated
  // title in a non-EN UI returned nothing (KD-3450).
  if (locale) {
    url += `&locale=${locale}`;
  }

  if (isMarketPlaceSource) {
    url += `&standaloneExperiencesOnly=${true}`;
  }

  if (searchedCountries.length > 0) {
    // Repeated param: the API binds countries to an array and rejects CSV.
    url += searchedCountries.map((id) => `&countries=${id}`).join('');
  }

  if (page) {
    url += `&page=${page}`;
  }

  if (!shouldSearchAdditionalFields) {
    url += `&searchDescriptions=${false}`;
  }

  // KD-3547 — back-office pickers list products in every state (available,
  // in progress, stand by); the API defaults to available only, so the flag is
  // sent only when a call site opts in.
  if (includeUnavailable) {
    url += `&includeUnavailable=${true}`;
  }

  // /internal/search returns a paginated envelope { data, meta } instead of a
  // raw array. Unwrap to { data, meta }. Let request failures propagate (503
  // while the ES index builds, but also timeouts / 5xx / dropped connections)
  // so the caller can tell a *failed* search from a genuinely *empty* one.
  // Previously every non-503 error was swallowed into { data: [] } and rendered
  // as "No results found", which hid latency/connectivity failures (KD-3515).
  return get(url).then((res) => ({
    data: res?.data?.data ?? [],
    meta: res?.data?.meta,
  }));
}
